export class Sequence {
    id?: number;
    code: string = '';
    current: number = 0;
    prefix:string = '';
    suffix: string = '';
    fill: number = 5;
    rectificative_code: string = '';
    rectificative_current: number = 0;
    rectificative_prefix: string = '';
    rectificative_suffix: string = '';
    rectificative_fill: number = 5;
    is_for_recurring_contracts: boolean = false;
    company_id?: number;
    personalization_id?: number;
    next_number?: string;
    next_rectificative_number?: string;
    min_number?: number;
    min_inv_date?: string;
    min_rectificative_number?: number;
    min_rectificative_inv_date?: string;
    duplicateError?: boolean;
    rectDuplicateError?: boolean;
    normalComboError?: boolean;
    rectComboError?: boolean;
    deleted?: boolean = false;
    disabled?: boolean = false;

    constructor(
        id?: number,
        code: string = '',
        current: number = 0,
        prefix: string = '',
        suffix: string = '',
        fill: number = 5,
        rectificative_code: string = '',
        rectificative_current: number = 0,
        rectificative_prefix: string = '',
        rectificative_suffix: string = '',
        rectificative_fill: number = 5,
        is_for_recurring_contracts: boolean = false,
        company_id?: number,
        personalization_id?: number,
        next_number?: string,
        next_rectificative_number?: string,
        min_number: number = -1,
        min_inv_date?: string,
        min_rectificative_number: number = -1,
        min_rectificative_inv_date?: string,
        deleted?: boolean,
        disabled?: boolean

    ) {
        this.id = id;
        this.code = code;
        this.current = current;
        this.prefix = prefix;
        this.suffix = suffix;
        this.fill = fill;
        this.rectificative_code = rectificative_code;
        this.rectificative_current = rectificative_current;
        this.rectificative_prefix = rectificative_prefix;
        this.rectificative_suffix = rectificative_suffix;
        this.rectificative_fill = rectificative_fill;
        this.is_for_recurring_contracts = is_for_recurring_contracts;
        this.company_id = company_id;
        this.personalization_id = personalization_id;
        this.next_number = next_number;
        this.next_rectificative_number = next_rectificative_number;
        this.min_number = min_number;
        this.min_inv_date = min_inv_date;
        this.min_rectificative_number = min_rectificative_number;
        this.min_rectificative_inv_date = min_rectificative_inv_date;
        this.deleted = deleted;
        this.disabled = disabled;
    }
}


/**
 * Valida que los campos "code" y "rectificative_code" de cada secuencia sean
 * no vacíos y únicos (ignorando mayúsculas y espacios en blanco).
 * Además, asigna la propiedad "duplicateError" o "rectDuplicateError" a true en
 * aquellas secuencias que tengan valores duplicados.
 *
 * @param {Array<Object>} sequences - Array de secuencias.
 * @returns {boolean} - true si la validación es correcta, false en caso contrario.
 */
export function validateCodeSequences(sequences, invoiceableType) {
    let isValid = true;
    const codes = {};
    if (invoiceableType === 'proforma'){
      return isValid;
    }
  
    sequences.forEach(seq => {
      // Validación del código normal.
      const norm = seq.code ? seq.code.trim().toLowerCase() : "";
      if (norm === "") {
        isValid = false;
      } else {
        if (!codes[norm]) {
          codes[norm] = [];
        }
        codes[norm].push({ seq, field: "code" });
      }
  
      // Validación del código rectificativo.
      if (invoiceableType === 'invoice') {
        const rect = seq.rectificative_code ? seq.rectificative_code.trim().toLowerCase() : "";
        if (rect === "") {
          isValid = false;
        } else {
          if (!codes[rect]) {
            codes[rect] = [];
          }
          codes[rect].push({ seq, field: "rectificative_code" });
        }
      }
    });
  
    // Reiniciamos las banderas de error.
    sequences.forEach(seq => {
      seq.duplicateError = false;
      seq.rectDuplicateError = false;
    });
  
    // Recorremos el mapa y, si hay duplicados, marcamos las secuencias.
    Object.keys(codes).forEach(key => {
      if (codes[key].length > 1) {
        isValid = false;
        codes[key].forEach(item => {
          if (item.field === "code") {
            item.seq.duplicateError = true;
          } else if (item.field === "rectificative_code") {
            item.seq.rectDuplicateError = true;
          }
        });
      }
    });
  
    return isValid;
  }
  
  /**
   * Valida que las combinaciones de "prefix-suffix" y "rectificative_prefix-rectificative_suffix"
   * sean únicas en el array de secuencias (ignorando mayúsculas y espacios).
   * Se asignará la propiedad "normalComboError" o "rectComboError" a true en aquellas secuencias
   * que tengan duplicados.
   *
   * @param {Array<Object>} sequences - Array de secuencias.
   * @returns {boolean} - true si la validación es correcta, false en caso contrario.
   */
  export function validatePrefixSuffixSequences(sequences, invoiceableType) {
    let isValid = true;
    if (invoiceableType === 'proforma'){
      return isValid;
    }
    // Reiniciamos las banderas de error.
    sequences.forEach(seq => {
      seq.normalComboError = false;
      seq.rectComboError = false;
    });
  
    const comboGroups = {};
  
    sequences.forEach(seq => {
      // Combinación normal: prefix y suffix.
      const normPrefix = seq.prefix ? seq.prefix.trim().toLowerCase() : "";
      const normSuffix = seq.suffix ? seq.suffix.trim().toLowerCase() : "";
      const normalKey = `${normPrefix}-${normSuffix}`;
      if (!comboGroups[normalKey]) {
        comboGroups[normalKey] = [];
      }
      comboGroups[normalKey].push({ seq, type: "normal" });
  
      if (invoiceableType === 'invoice') {
        // Combinación rectificativa: rectificative_prefix y rectificative_suffix.
        const recPrefix = seq.rectificative_prefix ? seq.rectificative_prefix.trim().toLowerCase() : "";
        const recSuffix = seq.rectificative_suffix ? seq.rectificative_suffix.trim().toLowerCase() : "";
        const rectKey = `${recPrefix}-${recSuffix}`;
        if (!comboGroups[rectKey]) {
          comboGroups[rectKey] = [];
        }
        comboGroups[rectKey].push({ seq, type: "rect" });
    }
    });
  
    // Se marcan las secuencias con combinaciones duplicadas.
    Object.keys(comboGroups).forEach(key => {
      if (comboGroups[key].length > 1) {
        isValid = false;
        comboGroups[key].forEach(item => {
          if (item.type === "normal") {
            item.seq.normalComboError = true;
          } else if (item.type === "rect") {
            item.seq.rectComboError = true;
          }
        });
      }
    });
  
    return isValid;
  }

      /**
     * Valida que el "próx. número" sea mayor a 0.
     * @param nextNumber Número a validar.
     * @returns {boolean} true si es mayor a 0, false en caso contrario.
     */
  export function validateNextNumber(nextNumber: number): boolean {
      return nextNumber > 0;
  }
  